import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { outfitAPI } from '../api';
import './OutfitDetailPage.css';

function OutfitDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [outfit, setOutfit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showFullImage, setShowFullImage] = useState(false);

  useEffect(() => {
    loadOutfitDetails();
  }, [id]);

  const loadOutfitDetails = async () => {
    try {
      setLoading(true);
      const response = await outfitAPI.getOutfits();
      const outfits = response.data;
      const foundOutfit = outfits.find(o => o.id === id);
      
      if (!foundOutfit) {
        setError('Outfit not found');
      } else {
        setOutfit(foundOutfit);
      }
      setError('');
    } catch (err) {
      console.error('Failed to load outfit:', err);
      setError('Failed to load outfit details');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFavorite = async () => {
    try {
      await outfitAPI.toggleFavorite(outfit.id);
      setOutfit({ ...outfit, is_favorite: !outfit.is_favorite });
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this outfit?')) {
      try {
        await outfitAPI.deleteOutfit(outfit.id);
        navigate('/outfits');
      } catch (err) {
        console.error('Failed to delete outfit:', err);
        setError('Failed to delete outfit');
      }
    }
  };

  if (loading) {
    return (
      <div className="outfit-detail-page">
        <div className="loading">Loading outfit details...</div>
      </div>
    );
  }

  if (error || !outfit) {
    return (
      <div className="outfit-detail-page">
        <div className="error-message">{error || 'Outfit not found'}</div>
        <button className="btn btn-primary" onClick={() => navigate('/outfits')}>
          Back to Outfits
        </button>
      </div>
    );
  }

  return (
    <div 
      className="outfit-detail-page"
      style={{ 
        backgroundImage: `url(${process.env.PUBLIC_URL}/assets/branding/hero-fashion.png)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      <div className="detail-header">
        <button className="btn btn-secondary back-btn" onClick={() => navigate('/outfits')}>
          ← Back to Outfits
        </button>
        <div className="header-actions">
          <button
            className={`btn ${outfit.is_favorite ? 'btn-favorite active' : 'btn-secondary'}`}
            onClick={handleToggleFavorite}
          >
            {outfit.is_favorite ? '★ Favorited' : '☆ Add to Favorites'}
          </button>
          <button className="btn btn-danger" onClick={handleDelete}>
            🗑️ Delete
          </button>
        </div>
      </div>
      <div className="detail-content">
        <div className="detail-image-section">
          <div className="main-image" onClick={() => setShowFullImage(true)} style={{cursor: 'zoom-in'}}>
            {outfit.thumbnail_url ? (
              <img src={outfit.thumbnail_url} alt={outfit.name} />
            ) : (
              <div className="placeholder-grid">
                {outfit.items.slice(0, 3).map((item) => (
                  <img 
                    key={item.id} 
                    src={item.extracted_cloth_url} 
                    alt={item.item_name}
                  />
                ))}
              </div>
            )}
          </div>
          <p style={{textAlign: 'center', color: '#718096', fontSize: '0.9rem', marginTop: '0.5rem'}}>
            🔍 Click image to view full size
          </p>
        </div>

        <div className="detail-info-section">
          <h1>{outfit.name}</h1>
          
          {outfit.description && (
            <div className="info-block">
              <h3>Description</h3>
              <p>{outfit.description}</p>
            </div>
          )}

          <div className="info-block">
            <h3>Details</h3>
            <div className="detail-tags">
              {outfit.occasion && (
                <div className="detail-tag">
                  <span className="tag-label">🎯 Occasion:</span>
                  <span className="tag tag-occasion">{outfit.occasion}</span>
                </div>
              )}
              {outfit.season && (
                <div className="detail-tag">
                  <span className="tag-label">🌤️ Season:</span>
                  <span className="tag tag-season">{outfit.season}</span>
                </div>
              )}
              <div className="detail-tag">
                <span className="tag-label">📅 Created:</span>
                <span className="tag">{new Date(outfit.created_at).toLocaleDateString()}</span>
              </div>
              {outfit.updated_at && outfit.updated_at !== outfit.created_at && (
                <div className="detail-tag">
                  <span className="tag-label">🔄 Updated:</span>
                  <span className="tag">{new Date(outfit.updated_at).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          </div>

          <div className="info-block">
            <h3>Items ({outfit.items.length})</h3>
            <div className="items-list">
              {outfit.items.map((item) => (
                <div key={item.id} className="item-card-small">
                  <div className="item-image">
                    <img src={item.extracted_cloth_url} alt={item.item_name} />
                  </div>
                  <div className="item-info">
                    <div className="item-name">{item.item_name}</div>
                    {item.item_type && (
                      <span className="tag tag-type">{item.item_type}</span>
                    )}
                    {item.color && (
                      <span className="tag tag-color">{item.color}</span>
                    )}
                    {item.pattern && (
                      <span className="tag tag-pattern">{item.pattern}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {showFullImage && (
        <div className="fullscreen-modal" onClick={() => setShowFullImage(false)}>
          <div className="fullscreen-content">
            <button className="close-fullscreen" onClick={() => setShowFullImage(false)}>
              ✕
            </button>
            {outfit.thumbnail_url ? (
              <img src={outfit.thumbnail_url} alt={outfit.name} />
            ) : (
              <div className="placeholder-grid-fullscreen">
                {outfit.items.slice(0, 3).map((item) => (
                  <img 
                    key={item.id} 
                    src={item.extracted_cloth_url} 
                    alt={item.item_name}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default OutfitDetailPage;
